import { Button, Text, Flex, Box } from '@chakra-ui/react';
import { ReactElement, useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ModalClassParticipantCreationVariantProps, SharedModalProps } from 'types/Modal';
import { MultiSelect } from 'components/common/Multiselect';
import { NovaIcon } from 'components/ui/novaIcon';
import { useUsers } from 'hooks/api/backend/managament/getB2BUsers';
import { useAddStudents } from 'hooks/api/backend/managament/getClasses';
import { CloseIcon } from 'assets/icons/v2';
import { useNovaToast } from 'hooks/utils/useNovaToast';
import { ToastVariant } from 'types/Toast';
import { toastContent } from 'config/toast';
import * as Sentry from '@sentry/react';
import { SchoolUserRole } from '@novaheal/types';
import { CustomModal } from '../CustomModal';
import { CustomModalContent } from '../CustomModalContent';

const SEARCH_DEBOUNCE_MS = 300;

type Props = SharedModalProps & ModalClassParticipantCreationVariantProps;

export const ClassParticipantCreationModal = ({
  handleClose,
  isShown,
  classId,
}: Props): ReactElement => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const { data: usersData, refetch } = useUsers({
    page: 1,
    searchQuery: debouncedSearchTerm || undefined,
    sortBy: 'firstName',
    sortOrder: 'asc',
    excludeClassId: classId,
    role: [SchoolUserRole.STUDENT],
    all: !debouncedSearchTerm,
    limit: debouncedSearchTerm ? 50 : undefined,
  });

  const queryClient = useQueryClient();
  const addStudentsMutation = useAddStudents();
  const toast = useNovaToast();
  const [selectedParticipants, setSelectedParticipants] = useState<string[]>([]);

  useEffect(() => {
    if (isShown) {
      refetch();
    }
  }, [isShown, refetch]);

  useEffect(() => {
    if (!isShown) {
      setSearchTerm('');
      setDebouncedSearchTerm('');
    }
  }, [isShown]);

  const handleSubmit = async (): Promise<void> => {
    if (selectedParticipants.length === 0 || !classId) {
      return;
    }

    try {
      const updatedClass = await addStudentsMutation.mutateAsync({
        classId,
        ids: selectedParticipants,
      });
      // Update cache with the response data instead of refetching
      queryClient.setQueryData(['class', classId], updatedClass); // queryClient.invalidateQueries({ queryKey: ['classes'] });
      toast.show({
        description: 'Teilnehmende erfolgreich hinzugefügt',
        variant: ToastVariant.SUCCESS,
      });
      handleClose();
      setSelectedParticipants([]);
    } catch (error) {
      toast.show({ ...toastContent.error.default, variant: ToastVariant.ERROR });
      Sentry.captureException(error);
    }
  };

  return (
    <CustomModal isOpen={isShown} onClose={handleClose} placement="center" size="md" hideHeader>
      <CustomModalContent p="30px" display="flex" flexDirection="column" gap="50px">
        <Flex flexDir="column" gap="30px" width="100%">
          <Flex flexDir="column" gap="l" width="100%">
            <Flex position="relative" alignItems="flex-start" width="100%">
              <Flex flexDir="column" gap="15px" flex="1">
                <Text textStyle="h3.medium" color="text.primary">
                  Teilnehmende hinzufügen
                </Text>
                <Text textStyle="body.m.regular" color="text.tertiary">
                  Wähle einen oder mehrere Benutzer aus, um sie als Teilnehmende dem ausgewählten
                  Kurs zuzuweisen.
                </Text>
              </Flex>
              <Box position="absolute" right="0" top="0" cursor="pointer" onClick={handleClose}>
                <NovaIcon icon={CloseIcon} size="l" color="icon.secondary" aria-hidden />
              </Box>
            </Flex>

            <Box width="100%">
              <MultiSelect
                placeholder="Namen auswählen"
                options={
                  usersData?.data?.map((user) => ({
                    id: user.id,
                    label: `${user.firstName} ${user.lastName || ''}`.trim(),
                    email: user.email,
                  })) || []
                }
                identifierKey="id"
                labelKey="label"
                secondaryKey="email"
                selectedValues={selectedParticipants}
                onChange={setSelectedParticipants}
                searchable
                searchPlaceholder="Benutzer suchen..."
                filterMode="server"
                onSearchChange={setSearchTerm}
                maxVisibleChips={2}
              />
            </Box>
          </Flex>
        </Flex>

        <Flex gap="5px" width="100%" justifyContent="flex-end">
          <Button onClick={handleClose} variant="tertiary" px="xl" py="10px" fontWeight="regular">
            Abbrechen
          </Button>
          <Button onClick={handleSubmit} variant="primary" px="xl" py="10px">
            Hinzufügen
          </Button>
        </Flex>
      </CustomModalContent>
    </CustomModal>
  );
};
